/**
 * IDDLBrowser — Phase 3a candidate-facing simulation primitive.
 *
 * Sandboxed web-search UI for the Information Search / Selection /
 * Interpretation concepts. Emits the 11 canonical browser events to
 * /api/attempts/{attemptId}/events.
 *
 * Behavioural fidelity > visual polish:
 *  • initial query input is empty — the candidate must type something
 *  • the first "Search" surfaces deliberately weak/noisy results
 *  • a clearly-labelled "Refine query" path lets us measure query_refined
 *  • each result has visible URL/domain — `Inspect source` button
 *    reveals deeper metadata (credibility/relevance reason)
 *  • the candidate selects a result with a radio + "Select as answer"
 *  • selecting a non-credible result is logged as `low_quality_selected`
 *    (auto-fail); selecting a credible one logs `final_decision`
 *    with value `select_credible_source` + the chosen result_id
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Search, Eye, BookmarkPlus, ArrowLeft, AlertTriangle, ShieldCheck, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";

const BROWSER_FINAL_VALUE = "select_credible_source";

export default function IDDLBrowser({
  attemptId,
  conceptId,
  scenarioText,
  onFinalize,
  onEventLogged,
}) {
  const { t } = useT();
  const queries = scenarioText?.queries || { vague: [], refined: [] };
  const researchQ = scenarioText?.research_question || "—";
  const vagueHint = scenarioText?.vague_query_hint || "";
  const refinedHint = scenarioText?.refined_query_hint || "";

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [phase, setPhase] = useState("initial"); // initial | vague | refined
  const [opened, setOpened] = useState(null);
  const [inspected, setInspected] = useState({});
  const [flagged, setFlagged] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [finalized, setFinalized] = useState(false);
  const browserFiredRef = useRef(false);

  useEffect(() => {
    if (browserFiredRef.current) return;
    browserFiredRef.current = true;
    emit("browser_opened", null, "browser_home");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function emit(eventName, eventValue, screenContext) {
    try {
      const r = await api
        .post(`/attempts/${attemptId}/events`, {
          concept_id: conceptId,
          event_name: eventName,
          event_value: eventValue,
          screen_context: screenContext,
          client_ts_ms: Date.now(),
        })
        .then((x) => x.data);
      if (onEventLogged && r?.event) onEventLogged(r.event);
      return r?.event;
    } catch (e) {
      toast.error(`Event ${eventName} failed: ${e?.response?.data?.detail || e.message}`);
    }
  }

  const runSearch = () => {
    const q = query.trim();
    if (!q) return;
    if (phase === "initial") {
      // First search ever — emit query_entered
      emit("query_entered", { q }, "search_results");
      setResults(queries.vague || []);
      setPhase("vague");
    } else {
      // Subsequent search — emit query_refined
      emit("query_refined", { q, previous_phase: phase }, "search_results");
      setResults(queries.refined || []);
      setPhase("refined");
    }
    setOpened(null);
    setInspected({});
    setSelectedId(null);
  };

  const openResult = (r) => {
    setOpened(r);
    emit("result_clicked", { result_id: r.id, url: r.url, domain: r.domain }, "result_view");
  };

  const ignoreResult = (r) => {
    emit("result_ignored", { result_id: r.id, url: r.url }, "search_results");
  };

  const backToList = () => {
    if (opened) emit("back_navigated", { from_result_id: opened.id }, "search_results");
    setOpened(null);
  };

  const inspectSource = (r) => {
    setInspected((p) => ({ ...p, [r.id]: true }));
    emit(
      "source_inspected",
      { result_id: r.id, url: r.url, domain: r.domain, credible: r.credible, relevant: r.relevant },
      "result_view",
    );
  };

  const flagCredibility = (r) => {
    setFlagged((p) => ({ ...p, [r.id]: true }));
    emit("source_credibility_flagged", { result_id: r.id, reason: r.reason }, "result_view");
  };

  const bookmark = (r) => {
    emit("bookmark_added", { result_id: r.id, url: r.url }, "result_view");
    toast.success("Bookmarked");
  };

  const selectAsAnswer = (r) => {
    if (finalized) return;
    setSelectedId(r.id);
    if (!r.credible) {
      // auto-fail
      emit(
        "low_quality_selected",
        { result_id: r.id, url: r.url, reason: r.reason },
        "result_view",
      );
    }
    emit(
      "final_decision",
      {
        decision: BROWSER_FINAL_VALUE,
        result_id: r.id,
        url: r.url,
        credible: r.credible,
        relevant: r.relevant,
      },
      "result_view",
    );
    setFinalized(true);
    if (onFinalize) onFinalize({ decision: BROWSER_FINAL_VALUE, resultId: r.id });
  };

  const currentList = useMemo(() => results, [results]);

  return (
    <div className="border border-[var(--iddl-rule)] bg-white" data-testid="iddl-browser">
      {/* Browser chrome */}
      <div className="border-b border-[var(--iddl-rule)] p-3 flex items-center gap-2 bg-[var(--iddl-paper)]">
        <span className="label-mono">IDDL Sandboxed Browser</span>
        <span className="ms-auto label-mono text-[var(--iddl-muted)]">
          {t("sim.browser.research_question")}:&nbsp;<strong>{researchQ}</strong>
        </span>
      </div>

      {/* Search bar */}
      <div className="p-3 border-b border-[var(--iddl-rule)] flex items-center gap-2">
        <Search size={14} className="text-[var(--iddl-muted)]" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
          placeholder={
            phase === "initial"
              ? (vagueHint ? `${t("sim.browser.search_placeholder")} (${vagueHint})` : t("sim.browser.search_placeholder"))
              : phase === "vague"
              ? (refinedHint ? `${t("sim.browser.btn_refine")} — ${refinedHint}` : t("sim.browser.btn_refine"))
              : t("sim.browser.btn_refine")
          }
          className="rounded-none flex-1"
          data-testid="iddl-browser-query-input"
          disabled={finalized}
        />
        <Button
          onClick={runSearch}
          className="rounded-none btn-ink"
          data-testid="iddl-browser-search-btn"
          disabled={finalized || !query.trim()}
        >
          {phase === "initial" ? t("sim.browser.btn_search") : t("sim.browser.btn_refine")}
        </Button>
      </div>

      {/* Results / opened view */}
      <div className="p-4 space-y-3 min-h-[360px]">
        {phase === "initial" && (
          <div className="text-sm text-[var(--iddl-muted)]" data-testid="iddl-browser-empty">
            {t("sim.browser.no_results")}
          </div>
        )}

        {!opened && phase !== "initial" && (
          <ul className="space-y-3" data-testid="iddl-browser-results">
            {currentList.map((r) => (
              <li key={r.id} className="border-b border-[var(--iddl-rule)] pb-3" data-testid={`iddl-result-${r.id}`}>
                <div className="text-xs text-[var(--iddl-muted)]">{r.domain}</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => openResult(r)}
                    className="text-sm text-blue-700 underline text-left"
                    data-testid={`iddl-result-open-${r.id}`}
                  >
                    {r.title}
                  </button>
                  <button
                    type="button"
                    onClick={() => ignoreResult(r)}
                    className="label-mono px-2 py-1 border border-[var(--iddl-rule)] hover:bg-[var(--iddl-paper)]"
                    data-testid={`iddl-result-ignore-${r.id}`}
                  >
                    Ignore
                  </button>
                </div>
                <p className="text-sm text-[var(--iddl-muted)] mt-1">{r.snippet}</p>
                <div className="label-mono mt-1 truncate">{r.url}</div>
              </li>
            ))}
          </ul>
        )}

        {opened && (
          <div className="space-y-3" data-testid="iddl-result-view">
            <Button
              onClick={backToList}
              variant="outline"
              size="sm"
              className="rounded-none"
              data-testid="iddl-btn-back"
            >
              <ArrowLeft size={14} className="mr-2" /> {t("sim.browser.btn_back")}
            </Button>

            <h3 className="font-display text-lg font-bold leading-tight">{opened.title}</h3>
            <div className="text-xs text-[var(--iddl-muted)]">
              {opened.domain} · <code className="label-mono">{opened.url}</code>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Button
                onClick={() => inspectSource(opened)}
                variant="outline"
                size="sm"
                className="rounded-none"
                data-testid="iddl-btn-inspect-source"
              >
                <Eye size={14} className="mr-2" /> {t("sim.browser.btn_inspect_source")}
              </Button>
              <Button
                onClick={() => flagCredibility(opened)}
                variant="outline"
                size="sm"
                className="rounded-none border-rose-500 text-rose-700"
                data-testid="iddl-btn-flag-source"
              >
                <Flag size={14} className="mr-2" /> {t("sim.browser.btn_flag_credibility")}
              </Button>
              <Button
                onClick={() => bookmark(opened)}
                variant="outline"
                size="sm"
                className="rounded-none"
                data-testid="iddl-btn-bookmark"
              >
                <BookmarkPlus size={14} className="mr-2" /> {t("sim.browser.btn_bookmark")}
              </Button>
            </div>

            {inspected[opened.id] && (
              <div className={`text-xs p-2 border ${opened.credible ? "border-emerald-400 bg-emerald-50" : "border-rose-400 bg-rose-50"}`} data-testid="iddl-source-detail">
                <div className="flex items-center gap-2 font-semibold">
                  {opened.credible ? <ShieldCheck size={14} /> : <AlertTriangle size={14} />}
                  Source metadata
                </div>
                <div className="mt-1">Domain authority: <strong>{opened.domain}</strong></div>
                <div>Credible: <strong>{String(opened.credible)}</strong></div>
                <div>Relevant: <strong>{String(opened.relevant)}</strong></div>
                <div>Notes: {opened.reason}</div>
              </div>
            )}

            <p className="text-sm whitespace-pre-wrap">{opened.snippet}</p>

            <div className="pt-3 border-t border-[var(--iddl-rule)]">
              <Button
                onClick={() => selectAsAnswer(opened)}
                disabled={finalized}
                className={`rounded-none ${opened.credible ? "btn-ink" : "bg-rose-600 hover:bg-rose-700 text-white"}`}
                data-testid="iddl-btn-select-answer"
              >
                {t("sim.browser.fd_select_credible")}
              </Button>
              {flagged[opened.id] && (
                <span className="label-mono ml-2 text-rose-700">Flagged · low credibility</span>
              )}
            </div>
          </div>
        )}

        {finalized && (
          <div className="text-sm border border-[var(--iddl-rule)] p-2 bg-[var(--iddl-paper)]" data-testid="iddl-browser-finalized">
            Final decision recorded · result_id <code>{selectedId}</code>. You can keep
            interacting — the latest <code>final_decision</code> event is what gets scored.
          </div>
        )}
      </div>
    </div>
  );
}
